import { useState, useEffect } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { api, apiRaw } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import Tooltip from "../components/Tooltip";

const PLAN_NAMES = { discovery: "Découverte", solo: "Solo", pro: "Pro", business: "Business" };

function UsageBar({ used, max, label, unit = "" }) {
  const unlimited = max === null || max === undefined;
  const pct = unlimited ? 0 : Math.min(100, (used / max) * 100);
  const danger = !unlimited && used >= max;
  const warn = !unlimited && pct >= 80 && !danger;
  return (
    <div style={{marginBottom:14}}>
      <div style={{display:'flex', justifyContent:'space-between', marginBottom:6, fontSize:13}}>
        <span style={{color:'var(--ink-muted)'}}>{label}</span>
        <span style={{fontWeight:600, fontFamily:'var(--font-mono)', color: danger ? 'var(--danger)' : warn ? 'var(--warn)' : 'var(--ink)'}}>
          {used} {unlimited ? "" : `/ ${max}`} {unit}
          {unlimited && <span style={{color:'var(--ok)', marginLeft:6, fontSize:11}}>∞</span>}
        </span>
      </div>
      <div style={{height:8, background:'var(--surface-2)', borderRadius:4, overflow:'hidden'}}>
        <div style={{
          height:'100%',
          width: unlimited ? '100%' : `${pct}%`,
          background: unlimited ? 'linear-gradient(90deg, var(--primary), var(--accent))' : danger ? 'var(--danger)' : warn ? 'var(--warn)' : 'linear-gradient(90deg, var(--primary), var(--accent))',
          borderRadius:4,
          transition:'width 400ms'
        }}></div>
      </div>
    </div>
  );
}

export default function Billing() {
  const { user, org, refresh } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const [plans, setPlans] = useState([]);
  const [usage, setUsage] = useState(null);
  const [period, setPeriod] = useState("monthly");
  const [loading, setLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [fecLoading, setFecLoading] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [trialEligible, setTrialEligible] = useState(false);

  useEffect(() => {
    api("/plans").then(r => setPlans(r.plans)).catch(() => {});
    api("/org/usage").then(setUsage).catch(() => {});
    api("/plans/trial-eligibility").then(r => setTrialEligible(r.eligible)).catch(() => {});
  }, []);

  // Poll status if we come back from checkout
  useEffect(() => {
    const params = new URLSearchParams(loc.search);
    const sessionId = params.get("session_id");
    const cancelled = params.get("cancelled");
    if (cancelled) {
      setErr("Paiement annulé. Vous pouvez réessayer quand vous voulez.");
      nav("/app/billing", { replace: true });
      return;
    }
    if (!sessionId) return;

    let attempts = 0;
    const max = 12;
    const poll = async () => {
      attempts++;
      try {
        const r = await api(`/plans/checkout/status/${sessionId}`);
        if (r.payment_status === "paid" || r.status === "complete") {
          const isTrial = r.subscription_status === "trialing";
          setMsg(isTrial
            ? `Essai 14 jours activé sur le plan ${PLAN_NAMES[r.plan_id]} ! Vous serez débité·e automatiquement à la fin de la période d'essai (annulation possible à tout moment).`
            : `Paiement validé ! Votre plan ${PLAN_NAMES[r.plan_id]} est actif.`);
          await refresh();
          api("/org/usage").then(setUsage).catch(() => {});
          api("/plans/trial-eligibility").then(rr => setTrialEligible(rr.eligible)).catch(() => {});
          nav("/app/billing", { replace: true });
          return;
        }
        if (r.status === "expired") {
          setErr("Session expirée. Veuillez réessayer.");
          return;
        }
      } catch (e) { /* ignore */ }
      if (attempts < max) setTimeout(poll, 2000);
      else setErr("Délai de vérification dépassé. Rafraîchissez la page si votre plan n'est pas à jour.");
    };
    poll();
  }, [loc.search, nav, refresh]);

  const upgrade = async (planId) => {
    setErr(""); setMsg(""); setLoading(true);
    try {
      const r = await api("/plans/checkout", {
        method: "POST",
        body: { plan_id: planId, period, origin_url: window.location.origin + "/app" }
      });
      window.location.href = r.url;
    } catch (e) {
      setErr(e.message || "Erreur");
      setLoading(false);
    }
  };

  const openPortal = async () => {
    setErr(""); setPortalLoading(true);
    try {
      const r = await api("/plans/portal", { method: "POST" });
      window.location.href = r.url;
    } catch (e) {
      setErr(e.message || "Le portail n'est pas disponible en mode test. En production, configurez vos clés Stripe personnelles.");
      setPortalLoading(false);
    }
  };

  const exportFEC = async () => {
    setErr(""); setFecLoading(true);
    try {
      const year = new Date().getFullYear();
      const res = await apiRaw(`/export/fec?year=${year}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = res.headers.get("content-disposition")?.match(/filename="([^"]+)"/)?.[1] || `FEC_${year}.txt`;
      a.click();
      URL.revokeObjectURL(url);
      setMsg("FEC téléchargé ✨");
    } catch (e) {
      setErr(e.status === 402 ? "L'export FEC est disponible uniquement sur le plan Business." : (e.message || "Erreur"));
    }
    setFecLoading(false);
  };

  const currentPlan = plans.find(p => p.id === org?.plan);
  const isOwner = user?.role === "owner";
  const canExportFEC = ["owner", "admin", "accountant"].includes(user?.role) && (org?.plan === "business" || (org?.trial_ends_at && new Date(org.trial_ends_at) > new Date()));
  const expires = org?.plan_expires_at ? new Date(org.plan_expires_at) : null;
  const trial = org?.trial_ends_at ? new Date(org.trial_ends_at) : null;
  const hasActivePaidPlan = org?.plan && org.plan !== "discovery" && expires && expires > new Date();

  return (
    <div className="content">
      <h1 style={{fontSize:30, marginBottom:8}}>Abonnement</h1>
      <p style={{color:'var(--ink-muted)', marginBottom:32}}>Gérez votre plan, consultez votre consommation et les fonctionnalités incluses.</p>

      {msg && <div className="panel" style={{background:'var(--ok-soft)', border:'1px solid var(--ok-soft)', color:'var(--ok)', marginBottom:20}} data-testid="billing-ok">{msg}</div>}
      {err && <div className="panel" style={{background:'var(--danger-soft)', border:'1px solid var(--danger-soft)', color:'var(--danger)', marginBottom:20}} data-testid="billing-err">{err}</div>}

      <div style={{display:'grid', gridTemplateColumns:'1.3fr 1fr', gap:20, marginBottom:24}}>
        <div className="panel" data-testid="current-plan">
          <div style={{fontSize:11.5, letterSpacing:'0.1em', textTransform:'uppercase', color:'var(--ink-muted)', fontWeight:600, marginBottom:4}}>Plan actuel</div>
          <h2>{currentPlan?.name || org?.plan} {org?.plan_period && <span style={{fontSize:14, color:'var(--ink-muted)', fontWeight:500}}>· {org.plan_period === "annual" ? "Annuel" : "Mensuel"}</span>}</h2>
          {/* Stripe trial : real subscription with CB, billed at end of trial */}
          {org?.subscription_status === "trialing" && expires && (
            <div style={{marginTop:10, padding:'10px 12px', background:'var(--surface-2)', borderRadius:8, fontSize:13, lineHeight:1.5, borderLeft:'3px solid var(--ok)'}}>
              <strong style={{color:'var(--ok)'}}>Abonnement sécurisé · en essai gratuit</strong><br/>
              Votre carte est enregistrée mais aucun débit ne sera effectué avant le {expires.toLocaleDateString("fr-FR", {day:"2-digit", month:"long", year:"numeric"})}. Vous pouvez annuler à tout moment depuis le portail.
            </div>
          )}
          {/* Backend trial : no CB, plan = discovery */}
          {org?.plan === "discovery" && trial && trial > new Date() && org?.subscription_status !== "trialing" && (
            <div style={{marginTop:10, padding:'10px 12px', background:'var(--surface-2)', borderRadius:8, fontSize:13, lineHeight:1.5, borderLeft:'3px solid var(--accent-2, #F26B5E)'}}>
              <strong>Essai Pro découverte · sans carte bancaire</strong><br/>
              Vous bénéficiez de toutes les fonctionnalités Pro jusqu'au {trial.toLocaleDateString("fr-FR", {day:"2-digit", month:"long", year:"numeric"})}.
              Pour ne pas perdre votre progression, ajoutez votre carte avant cette date — vous garderez 14 jours supplémentaires sans débit.
            </div>
          )}
          {expires && org?.subscription_status !== "trialing" && org?.plan !== "discovery" && (
            <div style={{marginTop:6, fontSize:14, color:'var(--ink-muted)'}}>Prochain renouvellement : {expires.toLocaleDateString("fr-FR", {day:"2-digit", month:"long", year:"numeric"})}</div>
          )}
          <div style={{marginTop:20, display:'flex', gap:8, flexWrap:'wrap', alignItems:'center'}}>
            {isOwner && hasActivePaidPlan && (
              <>
                <button className="btn btn-ghost btn-sm" onClick={openPortal} disabled={portalLoading} data-testid="portal-btn">
                  {portalLoading ? <span className="spinner"></span> : <>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>
                    Gérer mon abonnement
                  </>}
                </button>
                <Tooltip text="Ouvre le portail sécurisé Stripe : changez de plan, mettez à jour votre carte, téléchargez vos factures Stripe, ou annulez votre abonnement à tout moment." />
              </>
            )}
            {canExportFEC && (
              <>
                <button className="btn btn-ghost btn-sm" onClick={exportFEC} disabled={fecLoading} data-testid="fec-btn">
                  {fecLoading ? <span className="spinner"></span> : <>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                    Exporter FEC ({new Date().getFullYear()})
                  </>}
                </button>
                <Tooltip text="Le FEC (Fichier des Écritures Comptables) est le format officiel demandé par l'administration fiscale lors d'un contrôle. Format texte tab-séparé conforme aux exigences DGFiP. À transmettre à votre comptable." />
              </>
            )}
          </div>
        </div>

        <div className="panel" data-testid="usage-panel">
          <div style={{fontSize:11.5, letterSpacing:'0.1em', textTransform:'uppercase', color:'var(--ink-muted)', fontWeight:600, marginBottom:16}}>Consommation du mois</div>
          {usage ? (
            <>
              <UsageBar used={usage.factures_this_month} max={usage.max_factures_per_month} label="Factures ce mois" />
              <UsageBar used={usage.users_count + (usage.pending_invites||0)} max={usage.max_users} label="Utilisateurs (équipe + invitations)" />
              <UsageBar used={usage.clients_count} max={null} label="Clients enregistrés" />
            </>
          ) : (
            <div style={{textAlign:'center', padding:20}}><span className="spinner" style={{borderColor:'var(--border-strong)', borderTopColor:'var(--primary)', display:'inline-block'}}></span></div>
          )}
          {usage && usage.max_factures_per_month && usage.factures_this_month >= usage.max_factures_per_month * 0.8 && usage.factures_this_month < usage.max_factures_per_month && (
            <div style={{fontSize:12, color:'var(--warn)', marginTop:8, padding:'8px 10px', background:'var(--warn-soft)', borderRadius:8}}>⚠️ Vous approchez de la limite mensuelle. Passez à Solo pour un accès illimité.</div>
          )}
          {usage && usage.max_factures_per_month && usage.factures_this_month >= usage.max_factures_per_month && (
            <div style={{fontSize:12, color:'var(--danger)', marginTop:8, padding:'8px 10px', background:'var(--danger-soft)', borderRadius:8}}>🚫 Limite atteinte. Passez au plan supérieur pour créer plus de factures.</div>
          )}
        </div>
      </div>

      {isOwner && (
        <>
          <div style={{display:'flex', justifyContent:'center', marginBottom:24}}>
            <div className="period-toggle">
              <button className={period==="monthly"?"active":""} onClick={()=>setPeriod("monthly")} data-testid="billing-period-monthly">Mensuel</button>
              <button className={period==="annual"?"active":""} onClick={()=>setPeriod("annual")} data-testid="billing-period-annual">Annuel <span className="save">−20%</span></button>
            </div>
          </div>

          <div className="pricing-grid" data-testid="billing-plans">
            {plans.map(plan => {
              const isCurrent = plan.id === org?.plan;
              const price = period === "annual" ? plan.price_xpf_annual : plan.price_xpf_monthly;
              const perLabel = period === "annual" ? "/an" : "/mois";
              return (
                <div key={plan.id} className={`price-card ${plan.id==="pro"?"highlight":""}`} data-testid={`billing-plan-${plan.id}`}>
                  {plan.id==="pro" && <div className="badge-top">Populaire</div>}
                  <h3>{plan.name}</h3>
                  <div className="price">{plan.id==="discovery" ? "0" : price.toLocaleString("fr-FR")}<span className="unit"> F{plan.id==="discovery"?"":perLabel}</span></div>
                  <div style={{fontSize:13, color:'var(--ink-muted)', marginBottom:14}}>
                    {plan.id==="discovery" ? "Toujours gratuit" : `≈ ${period==="annual"?plan.price_eur_annual:plan.price_eur_monthly} €${perLabel}`}
                  </div>
                  <ul>
                    {plan.max_users && <li>{plan.max_users === 1 ? "1 utilisateur" : `Jusqu'à ${plan.max_users} utilisateurs`}</li>}
                    {plan.max_invoices_per_month ? <li>{plan.max_invoices_per_month} factures / mois</li> : <li>Factures illimitées</li>}
                    {plan.id !== "discovery" && <li>Relances auto · multi-entreprises</li>}
                    {plan.id === "pro" && <li>Paiement en ligne · portail client</li>}
                    {plan.id === "business" && <li>API · Export FEC · support prioritaire</li>}
                  </ul>
                  <div className="cta">
                    {isCurrent ? (
                      <button className="btn btn-ghost btn-full" disabled>✓ Plan actuel</button>
                    ) : plan.id === "discovery" ? (
                      <button className="btn btn-ghost btn-full" disabled>Plan gratuit</button>
                    ) : (
                      <>
                        <button className="btn btn-primary btn-full" onClick={()=>upgrade(plan.id)} disabled={loading} data-testid={`upgrade-${plan.id}`}>
                          {loading ? <span className="spinner"></span> : trialEligible ? `Essai 14 jours gratuit` : `Passer à ${plan.name}`}
                        </button>
                        {trialEligible && (
                          <div style={{fontSize:11, color:'var(--ink-muted)', textAlign:'center', marginTop:6}} data-testid={`trial-note-${plan.id}`}>
                            Carte requise · aucun débit avant J+14 · annulable à tout moment
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div style={{marginTop:30, padding:20, background:'var(--surface-2)', borderRadius:'var(--radius-md)', fontSize:13, color:'var(--ink-muted)'}}>
            <strong style={{color:'var(--ink)'}}>À propos du paiement</strong><br />
            Paiement sécurisé via Stripe. Tarifs facturés en euros à l'équivalence XPF/EUR (1 000 XPF ≈ 8,38 €). Sans engagement, annulable à tout moment depuis le portail de gestion.
          </div>
        </>
      )}
    </div>
  );
}
